import type { NormalizeOAS, OASModel } from 'fets';
import type openapi from '../../../rest-api/openapi';
import { ApiClient } from '../../../rest-api/fetsClient';
import type { CourseExportVersionListItem, ProcessingFailureKind } from '../components/CourseExportVersionList';
import type { CourseExportLink } from './courseExportLinksApi';

type AllowedDomainItem = OASModel<NormalizeOAS<typeof openapi>, 'AllowedDomainListingItem'>;
// Same schema the listing endpoint returns, so a tag is one type across both pages.
type CourseExportTag = OASModel<NormalizeOAS<typeof openapi>, 'CourseExportsListingQuery.TagDto'>;
// Reuses the list component's item type so the two never drift apart.
type CourseExportVersionListingItem = CourseExportVersionListItem;

type Result = {
  name: string;
  sasToken: string;
  publishedVersionGuid: string | null;
  versions: CourseExportVersionListingItem[];
  allowedDomains: AllowedDomainItem[];
  links: CourseExportLink[];
  tags: CourseExportTag[];
};

export const createCourseExportVersionApi = (apiClient: ApiClient) => {
  return async (courseExportGuid: string): Promise<Result> => {
    const response = await apiClient['/api/course-exports/{courseExportGuid}/versions'].get({
      params: { courseExportGuid },
    });
    const data = await response.json();
    const versions = data.items.map((item) => {
      const result: CourseExportVersionListingItem = {
        version: item.version,
        processedAtTimestamp: item.processedAtTimestamp ?? null,
        processingFailedAtTimestamp: item.processingFailedAtTimestamp ?? null,
        processingFailureKind: (item.processingFailureKind as ProcessingFailureKind) ?? null,
        createdAtTimestamp: item.createdAtTimestamp,
        publishedAtTimestamp: item.publishedAtTimestamp ?? null,
        description: item.description,
        guid: item.guid,
        changelog: item.changelog ?? null,
        scormTrackingQuizGuid: item.scormTrackingQuizGuid ?? null,
        source: {
          projectId: item.source.projectId ?? null,
          projectGuid: item.source.projectGuid ?? null,
          name: item.source.name,
          projectExists: item.source.projectExists,
          thumbnailUrl: item.source.thumbnailUrl ?? null,
          isPanomioProject: item.source.isPanomioProject,
          lastModifiedAtTimestamp: item.source.lastModifiedAtTimestamp ?? null,
        },
        supportedTrackingTypes: item.supportedTrackingTypes,
      };
      return result;
    });

    return {
      name: data.name,
      sasToken: data.sasToken,
      publishedVersionGuid: data.publishedVersionGuid ?? null,
      versions,
      allowedDomains: data.allowedDomains,
      links: data.links,
      tags: data.tags,
    };
  };
};
